import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaRolloApi } from "../crmPostventaRolloApi";
import Modal from "../../crmpostventa-shared/components/Modal";
import { LateTag, OrigenChip, PriorityChip, StatusChip } from "../components/Chips";
import { Muted, ResponsableCell, isCancelada, motivoLabel } from "../components/rolloCells";
import { Timeline } from "../gestionar/GestionarParts";
import { isClosed } from "../gestionar/gestionarModel";
import {
  CANAL_LABELS,
  EMPTY,
  codeOf,
  formatCuit,
  formatDate,
  formatDuration,
  formatRollos,
  formatTime,
  labelFor,
} from "../rolloFormat";

const TITLE_ID = "crm-rollo-detalle-title";
const PREVIOUS_ID = "crm-rollo-detalle-previas";
const SUMMARY_ID = "crm-rollo-detalle-resumen";
// The block shows the most recent previous deliveries (design rule, S9).
export const PREVIOUS_MAX = 5;

const isEntregada = (gestion) => codeOf(gestion?.estado) === "ENTREGADO";

// Observación written when the gestión was closed: the newest ENTREGAR / CANCELAR entry of the timeline.
const closingObservacion = (gestion, timeline) => {
  const accion = isEntregada(gestion) ? "ENTREGAR" : isCancelada(gestion) ? "CANCELAR" : null;
  if (!accion) return null;
  const entry = (timeline || []).find((item) => codeOf(item?.accion) === accion);
  return entry?.observacion || null;
};

const textOr = (value) => (value === null || value === undefined || String(value).trim() === "" ? <Muted /> : value);

function Item({ label, full = false, children }) {
  return (
    <div className={full ? "crm-rollo__sum-full" : undefined}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function CantidadEntregada({ gestion }) {
  const entregada = gestion?.cantidadEntregada;
  if (!isEntregada(gestion) || entregada === null || entregada === undefined) return <Muted />;
  if (Number(entregada) === Number(gestion.cantidadSolicitada)) return <>{formatRollos(entregada)}</>;
  return (
    <>
      <LateTag title={`Pedido: ${formatRollos(gestion.cantidadSolicitada)}`}>{formatRollos(entregada)}</LateTag>{" "}
      <Muted>difiere de lo pedido</Muted>
    </>
  );
}

function Duracion({ gestion, closed }) {
  if (closed) return <>{formatDuration(gestion?.diasAbierta)}</>;
  const dias = Number(gestion?.diasAbierta) || 0;
  return (
    <>
      {dias === 0 ? "Hoy" : formatDuration(dias)} <Muted>(abierta)</Muted>
    </>
  );
}

function SalidaCadete({ gestion }) {
  if (!gestion?.fechaSalida) return <Muted />;
  const hora = formatTime(gestion.fechaSalida);
  return <>{`${formatDate(gestion.fechaSalida)}${hora ? ` · ${hora}` : ""}`}</>;
}

// Summary of the gestión (design RolloDetail "Resumen"): every field, "—" when it does not apply.
function Resumen({ gestion, timeline, closed, currentUserId }) {
  const cancelled = isCancelada(gestion);
  const contacto = [gestion?.contactoNombre, gestion?.contactoTelefono].filter(Boolean).join(" · ");
  return (
    <section aria-labelledby={SUMMARY_ID}>
      <h3 id={SUMMARY_ID} className="crm-rollo__sec-title">
        Resumen de la gestión
      </h3>
      <dl className="crm-rollo__sum">
        <Item label={closed ? "Resultado" : "Estado"}>
          <StatusChip estado={gestion?.estado} />
        </Item>
        <Item label="Cantidad pedida">{formatRollos(gestion?.cantidadSolicitada)}</Item>
        <Item label="Cantidad entregada">
          <CantidadEntregada gestion={gestion} />
        </Item>
        <Item label="Fecha de pedido">{formatDate(gestion?.createdAt)}</Item>
        <Item label="Fecha de cierre">{formatDate(gestion?.fechaCierre)}</Item>
        <Item label="Duración">
          <Duracion gestion={gestion} closed={closed} />
        </Item>
        <Item label="Canal del pedido">{labelFor(CANAL_LABELS, gestion?.canalPedido)}</Item>
        <Item label="Origen">
          <OrigenChip origen={gestion?.origen} />
        </Item>
        <Item label="Prioridad">
          <PriorityChip prioridad={gestion?.prioridad} />
        </Item>
        <Item label="Responsable">
          <ResponsableCell item={gestion || {}} currentUserId={currentUserId} compact />
        </Item>
        <Item label="Creada por">{textOr(gestion?.usuarioCreacionNombre)}</Item>
        <Item label="Cadete">{textOr(gestion?.cadete)}</Item>
        <Item label="Salida del cadete">
          <SalidaCadete gestion={gestion} />
        </Item>
        {cancelled ? (
          <Item label="Motivo de cancelación">{motivoLabel(gestion)}</Item>
        ) : (
          <Item label="Recibió">{isEntregada(gestion) ? textOr(gestion?.recibioNombre) : <Muted />}</Item>
        )}
        <Item label="Fecha real de entrega">{isEntregada(gestion) ? formatDate(gestion?.fechaEntrega) : <Muted />}</Item>
        <Item label="Terminal">{textOr(gestion?.numeroTerminal)}</Item>
        <Item label="Fecha estimada de entrega">{formatDate(gestion?.fechaEstimadaEntrega)}</Item>
        <Item label="Contacto">{textOr(contacto)}</Item>
        <Item label="Dirección de entrega" full>
          {textOr(gestion?.direccionEntrega)}
        </Item>
        <Item label="Observación del pedido" full>
          {textOr(gestion?.observacion)}
        </Item>
        <Item label="Observación de cierre" full>
          {textOr(closingObservacion(gestion, timeline))}
        </Item>
      </dl>
    </section>
  );
}

// Previous deliveries of the comercio (design RolloDetailDelivered): it replaces "Seguimiento de rollos" of IPC.
function EntregasAnteriores({ entregas, total, onVerTodas }) {
  const shown = (entregas || []).slice(0, PREVIOUS_MAX);
  const count = Math.max(Number(total) || 0, shown.length);
  return (
    <section aria-labelledby={PREVIOUS_ID}>
      <div className="crm-rollo__sec-head">
        <h3 id={PREVIOUS_ID} className="crm-rollo__sec-title">
          Entregas anteriores de este comercio
        </h3>
        {count > shown.length ? (
          <button type="button" className="crm-rollo__link" onClick={onVerTodas}>
            {`Ver todas (${count})`}
          </button>
        ) : null}
      </div>
      {shown.length ? (
        <>
          <div className="crm-rollo__table-wrap">
            <table className="crm-rollo__table crm-rollo__table--sm">
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col" className="crm-rollo__num">
                    Cantidad
                  </th>
                  <th scope="col">Recibió</th>
                  <th scope="col">Cadete</th>
                  <th scope="col">Observación</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((entrega, index) => (
                  <tr key={entrega.id ?? index}>
                    <td className="crm-rollo__nowrap">{formatDate(entrega.fecha)}</td>
                    <td className="crm-rollo__num">{entrega.cantidad ?? EMPTY}</td>
                    <td className="crm-rollo__nowrap">{textOr(entrega.recibioNombre)}</td>
                    <td className="crm-rollo__nowrap">{textOr(entrega.cadete)}</td>
                    <td>{textOr(entrega.observacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {count > shown.length ? (
            <div className="crm-rollo__help">{`Mostrando las ${shown.length} más recientes de ${count}.`}</div>
          ) : null}
        </>
      ) : (
        <p className="crm-rollo__sec-empty">No hay entregas anteriores a esta gestión para este comercio.</p>
      )}
    </section>
  );
}

function DetalleHeader({ gestion }) {
  const ubicacion = [
    gestion?.cuit ? `CUIT ${formatCuit(gestion.cuit)}` : "",
    gestion?.numeroTerminal ? `Terminal ${gestion.numeroTerminal}` : "Sin terminal",
    gestion?.provincia || "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <>
      <span className="crm-rollo__s">{ubicacion}</span>
      <div className="crm-rollo__chips-row">
        <StatusChip estado={gestion?.estado} />
        <PriorityChip prioridad={gestion?.prioridad} />
      </div>
    </>
  );
}

/**
 * "Detalle de gestión" of Rollo (design RolloDetail, S7): read-only. Loads GET gestiones/{id} and shows the header,
 * the full summary, the previous deliveries of the comercio (last 5, "Ver todas (N)" when there are more) and the
 * timeline. A closed gestión says it is read-only; an active one offers "Gestionar" when the user may manage it.
 *
 * onVerTodas(gestion) and onGestionar(gestion) are expected to close this modal.
 */
export default function DetalleModal({ gestionId, row, currentUserId = null, canManage = true, darkMode = false, onClose, onGestionar, onVerTodas }) {
  const [detail, setDetail] = useState({ status: "loading", data: null, error: null });
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setDetail({ status: "loading", data: null, error: null });
    try {
      const response = await crmPostventaRolloApi.obtenerGestion(gestionId);
      if (requestId !== requestRef.current) return;
      setDetail({ status: "done", data: response || {}, error: null });
    } catch (loadError) {
      if (requestId !== requestRef.current) return;
      setDetail({ status: "error", data: null, error: loadError });
    }
  }, [gestionId]);

  useEffect(() => {
    load();
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  const data = detail.data;
  const gestion = data?.gestion || row || null;
  const closed = isClosed(gestion?.estado) || gestion?.activo === false;
  const canGestionar = detail.status === "done" && !closed && canManage && data?.puedeGestionar !== false;

  const renderMain = () => {
    if (detail.status === "loading") {
      return (
        <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando la gestión">
          {["60%", "85%", "45%", "70%"].map((width) => (
            <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
          ))}
        </div>
      );
    }

    if (detail.status === "error") {
      return (
        <div className="crm-rollo__note crm-rollo__note--error" role="alert">
          <span className="crm-rollo__note-ico" aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <b>No pudimos cargar la gestión</b>
          <p>{detail.error?.message || "Probá de nuevo en unos segundos."}</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={load}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      );
    }

    return (
      <>
        <Resumen gestion={gestion} timeline={data?.timeline} closed={closed} currentUserId={currentUserId} />
        <EntregasAnteriores
          entregas={data?.entregasAnteriores}
          total={data?.totalEntregasAnteriores}
          onVerTodas={() => onVerTodas?.(gestion)}
        />
      </>
    );
  };

  const footer = (
    <>
      <span className="crm-rollo__grow">{closed ? "Solo lectura · la gestión está cerrada" : ""}</span>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={onClose}>
        Cerrar
      </button>
      {canGestionar ? (
        <button type="button" className="crm-rollo__btn crm-rollo__btn--primary" onClick={() => onGestionar?.(gestion)}>
          Gestionar
        </button>
      ) : null}
    </>
  );

  return (
    <Modal
      titleId={TITLE_ID}
      title={gestion?.nombreComercio || "Gestión de rollo"}
      stepLabel={`Detalle de gestión · ${closed ? "Finalizada" : "Activa"}`}
      size="lg"
      darkMode={darkMode}
      onClose={onClose}
      headExtra={gestion ? <DetalleHeader gestion={gestion} /> : null}
      bodyClassName="crm-rollo__manage"
      footer={footer}
    >
      <div className="crm-rollo__manage-main">{renderMain()}</div>
      <aside className="crm-rollo__manage-side" aria-label="Línea de tiempo">
        {detail.status === "done" ? <Timeline entries={data?.timeline} gestion={gestion} /> : null}
      </aside>
    </Modal>
  );
}
